import clsx from 'clsx';
import React, { useState, useRef, useEffect } from 'react';
import { ResizeObserver } from '@juggle/resize-observer';

import { ComponentInternalAccordeonItem } from 'api/graphql-types';
import { sanitizeAndParseHtml } from 'utils/rich-text-renderer/sanitize-and-parse';
import { Icon } from 'components/icon/icon';
import Typography, { ITypographyHeadingVariants } from 'components/typography';

import FoldIn from 'public/assets/icons/fold-in.svg';
import FoldOut from 'public/assets/icons/fold-out.svg';
import CarotUp from 'public/assets/icons/carot-up.svg';
import CarotDown from 'public/assets/icons/carot-down.svg';

export interface IOpenItem {
	section: number;
	item: number;
}

interface ExtendedAccordeonItem extends ComponentInternalAccordeonItem {
	jsx?: React.JSX.Element;
}

export interface AccordeonItemProps {
	item?: ExtendedAccordeonItem;
	itemIndex: number;
	sectionIndex: number;
	backgroundColor?: string;
	openItem?: IOpenItem;
	setOpenItem: React.Dispatch<React.SetStateAction<IOpenItem | undefined>>;
	styles: any;
	variant: 'note' | 'module';
	headingType?: ITypographyHeadingVariants;
}

export const AccordeonItem = ({
	item,
	itemIndex,
	sectionIndex,
	backgroundColor,
	openItem,
	styles,
	variant,
	headingType,
	setOpenItem
}: AccordeonItemProps): React.JSX.Element => {
	const headerRef = useRef<HTMLSpanElement | null>(null);
	const bodyRef = useRef<HTMLDivElement | null>(null);
	const isNoteVariant = variant === 'note';
	const iconOpen = isNoteVariant ? <CarotUp /> : <FoldIn />;
	const iconClose = isNoteVariant ? <CarotDown /> : <FoldOut />;

	const [headerHeight, setHeaderHeight] = useState<string>('auto');
	const [bodyHeight, setBodyHeight] = useState<string>('auto');
	const [height, setHeight] = useState<string>('auto');

	const background = backgroundColor === 'white' ? styles.grey : backgroundColor === 'grey' && styles.white;
	const isOpen = openItem?.item === itemIndex && openItem?.section === sectionIndex;

	const onClick = (): void => {
		setOpenItem(isOpen ? undefined : { section: sectionIndex, item: itemIndex });
	};

	const onKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>): void => {
		if (event.key === 'Enter') {
			setOpenItem(isOpen ? undefined : { section: sectionIndex, item: itemIndex });
		}
	};

	// Individual observers for header and body to track changes in element size
	const headerObserver = useRef(
		new ResizeObserver((entries) => {
			const { blockSize } = entries[0].borderBoxSize[0];
			setHeaderHeight(String(blockSize));
		})
	);

	const bodyObserver = useRef(
		new ResizeObserver((entries) => {
			const { blockSize } = entries[0].borderBoxSize[0];
			setBodyHeight(String(blockSize));
		})
	);

	// Sets wrapper height when opened or on size change
	useEffect(() => {
		if (isOpen) {
			setHeight(`${Number(headerHeight) + Number(bodyHeight)}px`);
		} else {
			setHeight(`${headerHeight}px`);
		}
	}, [isOpen, headerHeight, bodyHeight]);

	// Assign observers to header and body ref when loaded
	useEffect(() => {
		if (headerRef.current) headerObserver.current.observe(headerRef.current);
		if (bodyRef.current) bodyObserver.current.observe(bodyRef.current);

		return () => {
			if (headerRef.current) headerObserver.current.unobserve(headerRef.current);
			if (bodyRef.current) bodyObserver.current.unobserve(bodyRef.current);
		};
	}, [headerRef, bodyRef]);

	return (
		<div className={styles.itemWrapper}>
			<div className={`${clsx(styles.item, isOpen && styles.openItem)} ${background}`} style={{ height }}>
				<span
					ref={headerRef}
					className={styles.itemHeader}
					onClick={onClick}
					role="button"
					tabIndex={0}
					onKeyDown={onKeyDown}
					aria-expanded={isOpen}
				>
					{item?.itemTitle && (
						<Typography tag={headingType || 'div'} variant="h4" className={styles.itemTitle}>
							{item.itemTitle}
						</Typography>
					)}
					<span className={styles.icon}>
						{isOpen ? <Icon customIcon={iconOpen} size="16px" /> : <Icon customIcon={iconClose} size="16px" />}
					</span>
				</span>

				<div className={styles.description} ref={bodyRef} tabIndex={-1} aria-hidden={!isOpen}>
					{item?.description && sanitizeAndParseHtml(item.description)}
					{item?.jsx && item.jsx}
				</div>
			</div>
		</div>
	);
};

export default AccordeonItem;
